import { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import CheckIcon from '@mui/icons-material/Check'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import LinkIcon from '@mui/icons-material/Link'
import { useCustomMutation, useNotification } from '@refinedev/core'
import AIFormatReview from './QuestionFormattingReview'
import QuestionCard from './QuestionCard'
import DraggableQuestionCard from './DraggableQuestionCard'
import InsertQuestionDialog from './InsertQuestionDialog'
import DeleteQuestionDialog from './DeleteQuestionDialog'
import {
  QuestionFormaterResponse,
  StructuredQuestion,
} from '../interfaces/questions'
import { computeSubNumber, applyNumberRename, renumberSequentially, shiftSubNumbers, getNumberingChanges, NumberingChange } from '../utils/questionUtils'

interface QuestionManagerProps {
  rawQuestions: string[]
  structuredQuestions: StructuredQuestion[]
  onQuestionsChange: (questions: StructuredQuestion[]) => void
  onActiveQuestionChange?: (question: StructuredQuestion | null) => void
  applicationLink?: string
}

export default function QuestionManager({
  rawQuestions,
  structuredQuestions,
  onQuestionsChange,
  onActiveQuestionChange,
  applicationLink,
}: QuestionManagerProps) {
  const [loading, setLoading] = useState(false)
  const [showAIReview, setShowAIReview] = useState(false)
  const [formatResult, setFormatResult] =
    useState<QuestionFormaterResponse | null>(null)
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<
    number | null
  >(null)
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState<
    number | null
  >(null)
  const [reorderMode, setReorderMode] = useState(false)
  const [pendingInsert, setPendingInsert] = useState<{
    atIndex: number
    position: 'above' | 'below'
  } | null>(null)
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null,
  )
  const [numberingChanges, setNumberingChanges] = useState<NumberingChange[]>([])
  const editStartNumberRef = useRef<string>('')
  const [extractionMode, setExtractionMode] = useState<'file' | 'url'>(
    applicationLink ? 'url' : 'file',
  )
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importUrl, setImportUrl] = useState(applicationLink || '')
  const [importLoading, setImportLoading] = useState(false)
  const [importedFromFile, setImportedFromFile] = useState(false)
  const [importSourceName, setImportSourceName] = useState('')
  const [extractionWarning, setExtractionWarning] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Sync importUrl when applicationLink becomes available (e.g. after data loads)
  // Only sets the default once — won't overwrite user edits since setImportUrl
  // uses the functional form to check current state without needing it as a dependency.
  useEffect(() => {
    if (applicationLink) {
      setImportUrl((current) => (current ? current : applicationLink))
    }
  }, [applicationLink])

  // Determine active question (editing takes precedence over selected)
  const activeQuestionIndex = editingQuestionIndex ?? selectedQuestionIndex

  // Notify parent when active question changes
  useEffect(() => {
    if (onActiveQuestionChange) {
      const activeQuestion =
        activeQuestionIndex !== null
          ? structuredQuestions[activeQuestionIndex]
          : null
      onActiveQuestionChange(activeQuestion)
    }
  }, [activeQuestionIndex, structuredQuestions, onActiveQuestionChange])

  const { open: openNotification } = useNotification()
  const { mutate: autoFormatQuestions } =
    useCustomMutation<QuestionFormaterResponse>()

  const { mutate: extractAndFormat } =
    useCustomMutation<QuestionFormaterResponse>()

  const hasRawQuestions = rawQuestions && rawQuestions.length > 0
  const hasStructuredQuestions = structuredQuestions.length > 0

  const handleConvertToStructured = async () => {
    if (!hasRawQuestions) return

    setLoading(true)

    // join questions into a single string for processing
    const rawText = rawQuestions.join('\n\n')
    autoFormatQuestions(
      {
        url: `/opportunities/questions/auto-format`,
        method: 'post',
        values: {
          raw_text: rawText,
        },
      },
      {
        onSuccess: (response) => {
          setFormatResult(response.data || null)
          setShowAIReview(true)
          setLoading(false)
          openNotification?.({
            message: 'Questions formatted successfully',
            type: 'success',
          })
        },
        onError: (error) => {
          setLoading(false)
          openNotification?.({
            message: 'Failed to format questions',
            description: error?.message || 'Please try again',
            type: 'error',
          })
        },
      },
    )
  }

  const canImport =
    extractionMode === 'file'
      ? importFile !== null
      : importUrl.trim().length > 0

  const handleImportFromSource = () => {
    if (!canImport) return

    setImportLoading(true)
    const formData = new FormData()
    if (extractionMode === 'file' && importFile) {
      formData.append('file', importFile)
    } else {
      formData.append('url', importUrl)
    }

    extractAndFormat(
      {
        url: `/opportunities/questions/extract-and-format`,
        method: 'post',
        values: formData,
        config: {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      },
      {
        onSuccess: (response) => {
          const data = response.data
          setImportLoading(false)

          // 0 questions extracted
          if (data && data.questions.length === 0) {
            setExtractionWarning(
              extractionMode === 'file'
                ? 'No questions could be extracted from this file. The document may not contain application questions, or the format may not be supported. Try a different file or enter questions manually.'
                : 'No questions could be extracted from this website. The site may be blocking automated access. Try uploading a PDF or DOCX version of the application form instead or enter questions manually.',
            )
            return
          }

          setExtractionWarning('')
          setFormatResult(data || null)
          setImportedFromFile(true)
          setImportSourceName(extractionMode === 'file' && importFile ? importFile.name : importUrl)
          setShowAIReview(true)
          openNotification?.({
            message: 'Questions extracted successfully',
            type: 'success',
          })
        },
        onError: (error: any) => {
          setImportLoading(false)
          const detail = error?.response?.data?.detail || error?.message || ''
          const isUrlMode = extractionMode === 'url'
          const isUrlError = isUrlMode && (detail.toLowerCase().includes('url') || error?.response?.status === 400)

          if (isUrlError) {
            setExtractionWarning(
              'Could not extract content from this website. The site may be blocking automated access or the URL may be invalid. Try uploading a PDF or DOCX version of the application form instead.',
            )
          } else {
            openNotification?.({
              message: 'Failed to extract questions',
              description: detail || 'Please try again',
              type: 'error',
            })
          }
        },
      },
    )
  }

  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setImportFile(file)
  }

  const handleImportUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportUrl(e.target.value)
  }

  const handleExtractionModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: 'file' | 'url' | null,
  ) => {
    if (newMode) {
      setExtractionMode(newMode)
      setExtractionWarning('')
    }
  }

  const handleImportReset = () => {
    setImportFile(null)
    setImportUrl(applicationLink || '')
    setImportLoading(false)
    setExtractionWarning('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRetry = () => {
    setShowAIReview(false)
    setFormatResult(null)
    if (importedFromFile) {
      // Return to the import form instead of re-running auto-format
      setImportedFromFile(false)

      return
    }
    handleConvertToStructured()
  }

  const handleAccept = (editedQuestions: StructuredQuestion[]) => {
    // ensure indices start at 0 and are sequential
    const reindexedQuestions = editedQuestions.map((q, i) => ({
      ...q,
      index: i,
    }))
    onQuestionsChange(reindexedQuestions)
    setShowAIReview(false)
    setImportedFromFile(false)

    handleImportReset()
    openNotification?.({
      message: 'Questions loaded for editing',
      type: 'success',
    })
  }

  const handleCloseReview = () => {
    setShowAIReview(false)
    setImportedFromFile(false)
  }

  const handleAddNewQuestion = () => {
    const newQuestion: StructuredQuestion = {
      index: structuredQuestions.length,
      question_number: '',
      question_type: 'text',
      question: '',
      attachment_required: false,
      required: true,
    }
    const newQuestions = [...structuredQuestions, newQuestion]
    const renumbered = renumberSequentially(newQuestions).map((q, i) => ({
      ...q,
      index: i,
    }))
    onQuestionsChange(renumbered)
    setEditingQuestionIndex(renumbered.length - 1)
  }

  const handleInsertConfirm = (choice: 'sub' | 'renumber') => {
    if (!pendingInsert) return
    const { atIndex, position } = pendingInsert
    const insertIndex = position === 'above' ? atIndex : atIndex + 1

    let questionNumber: string
    let sourceQuestions = structuredQuestions

    if (choice === 'sub') {
      const clickedQuestion = structuredQuestions[atIndex]
      const clickedSuffix =
        clickedQuestion.question_number.match(/[a-z]$/i)?.[0] ?? ''
      const isInsertAboveSub = position === 'above' && !!clickedSuffix

      if (isInsertAboveSub) {
        // Push existing subs down, take the clicked question's number
        const base = clickedQuestion.question_number.replace(/[a-z]$/i, '')
        sourceQuestions = shiftSubNumbers(structuredQuestions, base, clickedSuffix)
        questionNumber = clickedQuestion.question_number
      } else {
        const referenceIndex = position === 'above' ? atIndex - 1 : atIndex
        const referenceQuestion = structuredQuestions[referenceIndex]
        questionNumber =
          computeSubNumber(
            referenceQuestion.question_number,
            structuredQuestions,
          ) ?? String(insertIndex + 1)
      }
    } else {
      questionNumber = '' // renumberSequentially will assign the correct integer
    }

    const newQuestion: StructuredQuestion = {
      index: insertIndex,
      question_number: questionNumber,
      question_type: 'text',
      question: '',
      attachment_required: false,
      required: true,
    }

    const spliced = [...sourceQuestions]
    spliced.splice(insertIndex, 0, newQuestion)

    let finalQuestions: StructuredQuestion[]
    if (choice === 'renumber') {
      finalQuestions = renumberSequentially(spliced).map((q, i) => ({
        ...q,
        index: i,
      }))
    } else {
      finalQuestions = spliced.map((q, i) => ({ ...q, index: i }))
    }

    onQuestionsChange(finalQuestions)
    setEditingQuestionIndex(insertIndex)
    setPendingInsert(null)
  }

  const handleUpdateQuestion = (index: number, updated: StructuredQuestion) => {
    const newQuestions = [...structuredQuestions]
    newQuestions[index] = { ...updated, index }
    onQuestionsChange(newQuestions)
  }

  const handleUpdateQuestionNumber = (index: number, newNumber: string) => {
    if (!newNumber.trim()) return
    const trimmed = newNumber.trim()
    const isDuplicate = structuredQuestions.some(
      (q, i) => i !== index && q.question_number === trimmed,
    )
    if (isDuplicate) {
      openNotification?.({
        message: `Question number "${trimmed}" already exists`,
        type: 'error',
      })
      return
    }
    const oldNumber = structuredQuestions[index].question_number
    const updated = structuredQuestions.map((q, i) =>
      i === index ? { ...q, question_number: trimmed } : q,
    )
    const mapping = new Map([[oldNumber, trimmed]])
    const result = applyNumberRename(updated, mapping)
    onQuestionsChange(result)
    const changes = getNumberingChanges(result)
    if (changes.length > 0) setNumberingChanges(changes)
  }

  const handleDeleteConfirm = (remapTo: string | null) => {
    if (pendingDeleteIndex === null) return
    const index = pendingDeleteIndex
    const deletedNumber = structuredQuestions[index].question_number

    let updated = [...structuredQuestions]
    if (remapTo) {
      // Remap conditional_on/depends_on refs from deleted → target
      const mapping = new Map([[deletedNumber, remapTo]])
      updated = applyNumberRename(updated, mapping)
    }

    const filtered = updated
      .filter((_, i) => i !== index)
      .map((q, i) => ({ ...q, index: i }))

    const deletedNumbers = remapTo ? new Set<string>() : new Set([deletedNumber])
    const finalQuestions = renumberSequentially(filtered, deletedNumbers).map(
      (q, i) => ({ ...q, index: i }),
    )
    onQuestionsChange(finalQuestions)
    setPendingDeleteIndex(null)
    if (editingQuestionIndex === index) {
      setEditingQuestionIndex(null)
    }
  }

  const handleReorder = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return

    const newQuestions = [...structuredQuestions]
    const [movedQuestion] = newQuestions.splice(fromIndex, 1)
    newQuestions.splice(toIndex, 0, movedQuestion)

    const reindexed = renumberSequentially(newQuestions).map((q, i) => ({
      ...q,
      index: i,
    }))
    onQuestionsChange(reindexed)
  }

  return (
    <Box
      sx={{ mt: 1, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {!hasStructuredQuestions && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {hasRawQuestions
              ? "This opportunity doesn't have structured questions yet"
              : 'You can add questions in the text area above and use AI to convert them, or create structured questions manually below.'}
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {hasRawQuestions && (
              <Button
                type="button"
                variant="contained"
                onClick={handleConvertToStructured}
                disabled={loading || importLoading}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Converting...
                  </>
                ) : (
                  'Auto Convert'
                )}
              </Button>
            )}
            <Button
              type="button"
              variant={hasRawQuestions ? 'outlined' : 'contained'}
              startIcon={<AddIcon />}
              onClick={() => handleAddNewQuestion()}
              disabled={importLoading}
            >
              {hasRawQuestions
                ? 'Create Manually'
                : 'Create First Structured Question'}
            </Button>
          </Box>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              OR import from a document
            </Typography>
          </Divider>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Upload the funder's application form or provide a URL to
            automatically extract and create structured questions. You'll be
            able to review and edit before saving.
          </Typography>

          {extractionWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {extractionWarning}
            </Alert>
          )}

          <ToggleButtonGroup
            value={extractionMode}
            exclusive
            onChange={handleExtractionModeChange}
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="file">
              <UploadFileIcon sx={{ mr: 1 }} /> Upload File
            </ToggleButton>
            <ToggleButton value="url">
              <LinkIcon sx={{ mr: 1 }} /> From URL
            </ToggleButton>
          </ToggleButtonGroup>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {extractionMode === 'file' && (
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                disabled={importLoading}
              >
                {importFile ? importFile.name : 'Choose File (PDF or DOCX)'}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx"
                  hidden
                  onChange={handleImportFileChange}
                />
              </Button>
            )}

            {extractionMode === 'url' && (
              <>
                {applicationLink &&
                  importUrl === applicationLink &&
                  !extractionWarning && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: '100%' }}
                    >
                      You can change the URL below. This is pre-filled from
                      "Website link to application" field:{' '}
                      <a
                        href={applicationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {applicationLink}
                      </a>
                    </Typography>
                  )}
                <TextField
                  size="small"
                  placeholder="Application form URL"
                  value={importUrl}
                  onChange={handleImportUrlChange}
                  disabled={importLoading}
                  InputProps={{
                    startAdornment: (
                      <LinkIcon sx={{ mr: 1, color: 'action.active' }} />
                    ),
                  }}
                  sx={{ minWidth: 280 }}
                />
              </>
            )}

            <Button
              variant="contained"
              size="small"
              onClick={handleImportFromSource}
              disabled={!canImport || importLoading}
            >
              {importLoading ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Extracting...
                </>
              ) : (
                'Import Questions'
              )}
            </Button>
          </Box>
        </Paper>
      )}

      {hasStructuredQuestions && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {structuredQuestions.length}{' '}
              {structuredQuestions.length === 1 ? 'question' : 'questions'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                type="button"
                startIcon={reorderMode ? <CheckIcon /> : <SwapVertIcon />}
                variant={reorderMode ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setReorderMode(!reorderMode)
                  setEditingQuestionIndex(null)
                }}
              >
                {reorderMode ? 'Done Reordering' : 'Reorder'}
              </Button>
              <Button
                type="button"
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                onClick={handleAddNewQuestion}
                disabled={reorderMode}
              >
                Add Question
              </Button>
            </Box>
            <Button
              type="button"
              variant="outlined"
              color="error"
              size="small"
              onClick={() => {
                onQuestionsChange([])
              }}
            >
              Clear All
            </Button>
          </Box>
          <Box
            sx={{
              mb: 2,
              flex: 1,
              overflowY: 'auto',
              border: '1px solid #ccc',
              borderRadius: 1,
              px: 3,
              py: 2,
              minWidth: 800,
            }}
          >
            {reorderMode ? (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Drag and drop questions to reorder them. Click "Done
                  Reordering" when finished.
                </Alert>
                {structuredQuestions.map((question, index) => (
                  <DraggableQuestionCard
                    key={`question-${index}`}
                    question={question}
                    index={index}
                    onReorder={handleReorder}
                    onUpdateQuestionNumber={(num) =>
                      handleUpdateQuestionNumber(index, num)
                    }
                    onInsertAbove={() =>
                      setPendingInsert({ atIndex: index, position: 'above' })
                    }
                    onInsertBelow={() =>
                      setPendingInsert({ atIndex: index, position: 'below' })
                    }
                  />
                ))}
              </>
            ) : (
              structuredQuestions.map((question, index) => (
                <Box
                  key={`question-${index}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedQuestionIndex(
                      selectedQuestionIndex === index ? null : index,
                    )
                  }}
                  sx={{
                    borderLeft:
                      activeQuestionIndex === index
                        ? '3px solid #ffc107'
                        : '3px solid transparent',
                    backgroundColor:
                      activeQuestionIndex === index
                        ? 'rgba(255, 193, 7, 0.1)'
                        : 'transparent',
                    pl: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <QuestionCard
                    question={question}
                    allQuestions={structuredQuestions}
                    onEdit={() => {
                      if (editingQuestionIndex === index) {
                        // Closing edit — validate text and number, then check
                        // for gaps
                        const currentNumber =
                          structuredQuestions[index].question_number
                        if (!structuredQuestions[index].question.trim()) {
                          openNotification?.({
                            message: `Question "${currentNumber}" needs question text`,
                            type: 'error',
                          })
                          return // keep in edit mode
                        }
                        const isDuplicate = structuredQuestions.some(
                          (q, i) =>
                            i !== index &&
                            q.question_number === currentNumber,
                        )
                        if (isDuplicate) {
                          openNotification?.({
                            message: `Question number "${currentNumber}" already exists`,
                            type: 'error',
                          })
                          return // keep in edit mode
                        }
                        setEditingQuestionIndex(null)
                        if (currentNumber !== editStartNumberRef.current) {
                          const changes =
                            getNumberingChanges(structuredQuestions)
                          if (changes.length > 0)
                            setNumberingChanges(changes)
                        }
                      } else {
                        // Opening edit — store the starting number
                        editStartNumberRef.current =
                          structuredQuestions[index].question_number
                        setEditingQuestionIndex(index)
                      }
                    }
                    }
                    onUpdate={(updated) => handleUpdateQuestion(index, updated)}
                    onDelete={() => setPendingDeleteIndex(index)}
                    onInsertAbove={() =>
                      setPendingInsert({ atIndex: index, position: 'above' })
                    }
                    onInsertBelow={() =>
                      setPendingInsert({ atIndex: index, position: 'below' })
                    }
                    isEditing={editingQuestionIndex === index}
                  />
                </Box>
              ))
            )}
          </Box>
        </>
      )}

      <AIFormatReview
        open={showAIReview}
        rawText={importedFromFile ? '' : rawQuestions.join('\n\n')}
        formatResult={formatResult}
        onClose={handleCloseReview}
        onRetry={handleRetry}
        onAccept={handleAccept}
        hideRawText={importedFromFile}
        sourceName={importedFromFile ? importSourceName : undefined}
      />
      <InsertQuestionDialog
        open={pendingInsert !== null}
        atIndex={pendingInsert?.atIndex ?? 0}
        position={pendingInsert?.position ?? 'below'}
        allQuestions={structuredQuestions}
        onConfirm={handleInsertConfirm}
        onClose={() => setPendingInsert(null)}
      />
      <DeleteQuestionDialog
        open={pendingDeleteIndex !== null}
        question={
          pendingDeleteIndex !== null
            ? structuredQuestions[pendingDeleteIndex]
            : null
        }
        allQuestions={structuredQuestions}
        onConfirm={handleDeleteConfirm}
        onClose={() => setPendingDeleteIndex(null)}
      />
      <Dialog
        open={numberingChanges.length > 0}
        onClose={() => setNumberingChanges([])}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Renumber questions?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The following questions would be renumbered:
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {numberingChanges.map((change) => (
              <Box key={change.from} sx={{ mb: 0.5 }}>
                <Typography variant="body2">
                  <strong>Q{change.from}</strong> → <strong>Q{change.to}</strong>
                  {change.questionText
                    ? ` — ${change.questionText.slice(0, 60)}${change.questionText.length > 60 ? '...' : ''}`
                    : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNumberingChanges([]); if (reorderMode) setReorderMode(false) }}>Keep as is</Button>
          <Button
            variant="contained"
            onClick={() => {
              const renumbered = renumberSequentially(structuredQuestions).map(
                (q, i) => ({ ...q, index: i }),
              )
              onQuestionsChange(renumbered)
              setNumberingChanges([])
              if (reorderMode) setReorderMode(false)
            }}
          >
            Renumber
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
